import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CONTENT_TYPE_OPTIONS,
  parseStructure,
  parseWizardQuestions,
  updateExcerptusTemplate,
  updateUserTemplate,
  wizardFieldKind,
  type ExcerptusTemplate,
  type ReferenceSource,
  type StructureSection,
  type UserTemplate,
  type VoiceLean,
  type WizardField,
  type WizardQuestion,
} from "@/lib/composerTemplates";
import { useWorkspace } from "@/hooks/useWorkspace";
import { ChipTextArea, ChipTextInput } from "@/components/composer/ChipTextField";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "user" | "excerptus";
  template: UserTemplate | ExcerptusTemplate;
  onSaved?: () => void;
};

const FIELD_KIND_OPTIONS: Array<{ value: WizardField["kind"]; label: string; hint: string }> = [
  { value: "text", label: "Text", hint: "Free-form paragraph answer." },
  { value: "choice", label: "Choice", hint: "Pick from a fixed list of options." },
  { value: "number", label: "Number", hint: "Numeric answer (e.g. target word count)." },
  { value: "slider", label: "Slider", hint: "Range with labeled endpoints." },
  { value: "date", label: "Date", hint: "Single date value." },
  { value: "reference_picker", label: "Reference picker", hint: "Attach chips from Corpus, Library, Excerpts, Notes, Podcasts, or Highlights." },
];

const REFERENCE_SOURCE_LABELS: Record<ReferenceSource, string> = {
  excerpts: "My Excerpts",
  library: "My Library",
  corpus: "Corpus",
  notes: "Notes",
  podcasts: "Podcasts",
};

const VOICE_LEAN_INHERIT = "__inherit__";
const VOICE_LEAN_OPTIONS: Array<{ value: typeof VOICE_LEAN_INHERIT | VoiceLean; label: string }> = [
  { value: VOICE_LEAN_INHERIT, label: "— inherit avatar blend —" },
  { value: "mode_a", label: "Lean Mode A" },
  { value: "mode_b", label: "Lean Mode B" },
  { value: "blend", label: "Explicit blend" },
];

const csvToList = (s: string): string[] =>
  s.split(",").map((x) => x.trim()).filter(Boolean);
const listToCsv = (l: string[] | null | undefined): string =>
  (l ?? []).join(", ");

const defaultFieldFor = (kind: WizardField["kind"]): WizardField => {
  switch (kind) {
    case "text": return { kind: "text", multiline: true };
    case "choice": return { kind: "choice", options: [], multi: false };
    case "number": return { kind: "number" };
    case "slider": return { kind: "slider", min: 1, max: 10 };
    case "date": return { kind: "date" };
    case "reference_picker":
      return { kind: "reference_picker", sources: ["corpus", "library", "excerpts"], multi: true };
  }
};

export const TemplateEditor = ({ open, onOpenChange, mode, template, onSaved }: Props) => {
  const { active } = useWorkspace();
  const workspaceId = active?.id ?? null;
  // Chip fields need a workspace to scope the `/` Corpus picker. Both User and
  // Excerptus (admin) templates get chip authoring when a workspace is active
  // — admin-only mode gate lifted 2026-07-09 so curated templates can reference
  // admin-workspace Corpus items during authoring.
  const chipsEnabled = Boolean(workspaceId);
  const [name, setName] = useState(template.name);
  const [contentType, setContentType] = useState(template.content_type);
  const [description, setDescription] = useState(template.description ?? "");
  const [introMessage, setIntroMessage] = useState(template.intro_message ?? "");
  const [questions, setQuestions] = useState<WizardQuestion[]>(parseWizardQuestions(template.wizard_questions));
  const [structure, setStructure] = useState<StructureSection[]>(parseStructure(template.default_structure));
  const [isPublished, setIsPublished] = useState<boolean>(
    mode === "excerptus" ? Boolean((template as ExcerptusTemplate).is_published) : true,
  );
  const [sortOrder, setSortOrder] = useState<number>(template.sort_order ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(template.name);
    setContentType(template.content_type);
    setDescription(template.description ?? "");
    setIntroMessage(template.intro_message ?? "");
    setQuestions(parseWizardQuestions(template.wizard_questions));
    setStructure(parseStructure(template.default_structure));
    setSortOrder(template.sort_order ?? 0);
    if (mode === "excerptus") {
      setIsPublished(Boolean((template as ExcerptusTemplate).is_published));
    }
  }, [open, template, mode]);

  const addQuestion = () =>
    setQuestions((q) => [...q, { id: `q${q.length + 1}`, prompt: "", hint: "", required: false }]);
  const updateQuestion = (i: number, patch: Partial<WizardQuestion>) =>
    setQuestions((q) => q.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const updateField = (i: number, patch: Partial<WizardField>) =>
    setQuestions((q) =>
      q.map((x, idx) => {
        if (idx !== i) return x;
        const current = x.field ?? defaultFieldFor("text");
        return { ...x, field: { ...current, ...patch } as WizardField };
      }),
    );
  const setFieldKind = (i: number, kind: WizardField["kind"]) =>
    setQuestions((q) =>
      q.map((x, idx) => (idx === i ? { ...x, field: defaultFieldFor(kind) } : x)),
    );
  const removeQuestion = (i: number) =>
    setQuestions((q) => q.filter((_, idx) => idx !== i));

  const addSection = () =>
    setStructure((s) => [...s, { section: "", angle: "", goal: "" }]);
  const updateSection = (i: number, patch: Partial<StructureSection>) =>
    setStructure((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeSection = (i: number) =>
    setStructure((s) => s.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const cleanQuestions = questions
        .filter((q) => q.prompt.trim())
        .map((q) => ({
          id: q.id,
          prompt: q.prompt.trim(),
          hint: q.hint?.trim() || null,
          required: Boolean(q.required),
          // Persist field only when non-default so old rows stay lean.
          ...(q.field && q.field.kind !== "text" ? { field: q.field } : {}),
        }));
      const cleanStructure = structure
        .filter((s) => s.section.trim())
        .map((s) => ({
          section: s.section.trim(),
          angle: s.angle?.trim() || null,
          goal: s.goal?.trim() || null,
          target_word_count: s.target_word_count && s.target_word_count > 0 ? s.target_word_count : null,
          must_include: s.must_include && s.must_include.length > 0 ? s.must_include : null,
          must_avoid: s.must_avoid && s.must_avoid.length > 0 ? s.must_avoid : null,
          voice_lean: s.voice_lean ?? null,
        }));
      const payload = {
        name: name.trim(),
        content_type: contentType,
        description: description.trim() || null,
        intro_message: introMessage.trim() || null,
        wizard_questions: cleanQuestions,
        default_structure: cleanStructure,
        sort_order: sortOrder,
      };
      if (mode === "excerptus") {
        await updateExcerptusTemplate(template.id, { ...payload, is_published: isPublished });
      } else {
        await updateUserTemplate(template.id, payload);
      }
      toast.success("Template saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit template</DialogTitle>
          <DialogDescription>
            {mode === "excerptus"
              ? "Excerptus template — visible to all users when published."
              : "Workspace template — visible only inside this workspace."}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-3">
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="te-name">Name</Label>
              <Input id="te-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="te-type">Content type</Label>
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger id="te-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="te-sort">Sort order</Label>
                <Input
                  id="te-sort"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="te-desc">Description</Label>
              {chipsEnabled && workspaceId ? (
                <ChipTextArea
                  workspaceId={workspaceId}
                  value={description}
                  onChange={setDescription}
                  placeholder="Description — type / to reference a Corpus doc, folder, or bucket"
                  minHeight={64}
                />
              ) : (
                <Textarea id="te-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="te-intro">Intro message</Label>
              {chipsEnabled && workspaceId ? (
                <ChipTextArea
                  workspaceId={workspaceId}
                  value={introMessage}
                  onChange={setIntroMessage}
                  placeholder="Intro message — type / to reference a Corpus doc, folder, or bucket"
                  minHeight={88}
                />
              ) : (
                <Textarea
                  id="te-intro"
                  value={introMessage}
                  onChange={(e) => setIntroMessage(e.target.value)}
                  rows={3}
                />
              )}
            </div>

            {mode === "excerptus" && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="te-pub" className="text-sm">Published</Label>
                  <p className="text-xs text-muted-foreground">Visible to all users when on.</p>
                </div>
                <Switch id="te-pub" checked={isPublished} onCheckedChange={setIsPublished} />
              </div>
            )}

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Wizard questions</Label>
                <Button variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add question
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Adding any wizard questions makes this a <span className="font-medium">Dynamic</span> template
                (intake form runs before the planning agent). Leave empty to keep it
                <span className="font-medium"> Static</span> — the structure drops straight into the Draft Canvas.
              </p>
              {questions.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No questions yet. The chat will skip the wizard if empty.
                </p>
              )}
              {questions.map((q, i) => (
                <WizardQuestionEditor
                  key={i}
                  question={q}
                  workspaceId={chipsEnabled ? workspaceId : null}
                  onChange={(patch) => updateQuestion(i, patch)}
                  onFieldChange={(patch) => updateField(i, patch)}
                  onFieldKindChange={(kind) => setFieldKind(i, kind)}
                  onRemove={() => removeQuestion(i)}
                />
              ))}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Default structure</Label>
                <Button variant="outline" size="sm" onClick={addSection}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add section
                </Button>
              </div>
              {structure.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No sections defined. The planner will propose a structure from a blank slate.
                </p>
              )}
              {structure.map((s, i) => (
                <SectionEditor
                  key={i}
                  section={s}
                  workspaceId={chipsEnabled ? workspaceId : null}
                  onChange={(patch) => updateSection(i, patch)}
                  onRemove={() => removeSection(i)}
                />
              ))}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Per-question editor ----------

type WizardQuestionEditorProps = {
  question: WizardQuestion;
  workspaceId: string | null;
  onChange: (patch: Partial<WizardQuestion>) => void;
  onFieldChange: (patch: Partial<WizardField>) => void;
  onFieldKindChange: (kind: WizardField["kind"]) => void;
  onRemove: () => void;
};

const WizardQuestionEditor = ({
  question,
  workspaceId,
  onChange,
  onFieldChange,
  onFieldKindChange,
  onRemove,
}: WizardQuestionEditorProps) => {
  const kind = wizardFieldKind(question);
  const kindHint = useMemo(
    () => FIELD_KIND_OPTIONS.find((o) => o.value === kind)?.hint ?? "",
    [kind],
  );

  return (
    <div className="grid gap-2 rounded-md border p-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid gap-1.5">
          {workspaceId ? (
            <>
              <ChipTextInput
                workspaceId={workspaceId}
                value={question.prompt}
                onChange={(v) => onChange({ prompt: v })}
                placeholder="Question prompt (type / to reference Corpus)"
              />
              <ChipTextInput
                workspaceId={workspaceId}
                value={question.hint ?? ""}
                onChange={(v) => onChange({ hint: v })}
                placeholder="Hint (optional, type / for Corpus)"
              />
            </>
          ) : (
            <>
              <Input
                placeholder="Question prompt"
                value={question.prompt}
                onChange={(e) => onChange({ prompt: e.target.value })}
              />
              <Input
                placeholder="Hint (optional)"
                value={question.hint ?? ""}
                onChange={(e) => onChange({ hint: e.target.value })}
              />
            </>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <div className="grid gap-1.5">
          <Label className="text-xs text-muted-foreground">Field type</Label>
          <Select value={kind} onValueChange={(v) => onFieldKindChange(v as WizardField["kind"])}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 pt-5 text-xs text-muted-foreground">
          <Switch
            checked={Boolean(question.required)}
            onCheckedChange={(v) => onChange({ required: v })}
          />
          Required
        </label>
      </div>
      {kindHint && <p className="text-xs text-muted-foreground">{kindHint}</p>}

      <FieldConfigEditor
        field={question.field ?? defaultFieldFor("text")}
        onChange={onFieldChange}
      />
    </div>
  );
};

// ---------- Per-field-type config ----------

const FieldConfigEditor = ({
  field,
  onChange,
}: {
  field: WizardField;
  onChange: (patch: Partial<WizardField>) => void;
}) => {
  if (field.kind === "text") return null;

  if (field.kind === "choice") {
    return (
      <div className="grid gap-1.5">
        <Label className="text-xs text-muted-foreground">Options (comma-separated)</Label>
        <Input
          value={field.options.join(", ")}
          onChange={(e) =>
            onChange({ options: csvToList(e.target.value) } as Partial<WizardField>)
          }
          placeholder="e.g. Deep dive, Newsletter, Micro post"
        />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={Boolean(field.multi)}
            onCheckedChange={(v) => onChange({ multi: v } as Partial<WizardField>)}
          />
          Allow multiple selections
        </label>
      </div>
    );
  }

  if (field.kind === "number") {
    return (
      <div className="grid grid-cols-3 gap-2">
        <div className="grid gap-1.5">
          <Label className="text-xs text-muted-foreground">Min</Label>
          <Input
            type="number"
            value={field.min ?? ""}
            onChange={(e) =>
              onChange({ min: e.target.value === "" ? undefined : Number(e.target.value) } as Partial<WizardField>)
            }
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs text-muted-foreground">Max</Label>
          <Input
            type="number"
            value={field.max ?? ""}
            onChange={(e) =>
              onChange({ max: e.target.value === "" ? undefined : Number(e.target.value) } as Partial<WizardField>)
            }
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs text-muted-foreground">Unit</Label>
          <Input
            value={field.unit ?? ""}
            onChange={(e) => onChange({ unit: e.target.value } as Partial<WizardField>)}
            placeholder="e.g. words"
          />
        </div>
      </div>
    );
  }

  if (field.kind === "slider") {
    return (
      <div className="grid gap-2">
        <div className="grid grid-cols-3 gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Min</Label>
            <Input
              type="number"
              value={field.min}
              onChange={(e) => onChange({ min: Number(e.target.value) || 0 } as Partial<WizardField>)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Max</Label>
            <Input
              type="number"
              value={field.max}
              onChange={(e) => onChange({ max: Number(e.target.value) || 0 } as Partial<WizardField>)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Step</Label>
            <Input
              type="number"
              value={field.step ?? ""}
              onChange={(e) =>
                onChange({ step: e.target.value === "" ? undefined : Number(e.target.value) } as Partial<WizardField>)
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Min label</Label>
            <Input
              value={field.minLabel ?? ""}
              onChange={(e) => onChange({ minLabel: e.target.value } as Partial<WizardField>)}
              placeholder="e.g. Casual"
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs text-muted-foreground">Max label</Label>
            <Input
              value={field.maxLabel ?? ""}
              onChange={(e) => onChange({ maxLabel: e.target.value } as Partial<WizardField>)}
              placeholder="e.g. Formal"
            />
          </div>
        </div>
      </div>
    );
  }

  if (field.kind === "date") return null;

  if (field.kind === "reference_picker") {
    const current = new Set(field.sources);
    const toggle = (src: ReferenceSource) => {
      const next = new Set(current);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      onChange({ sources: Array.from(next) as ReferenceSource[] } as Partial<WizardField>);
    };
    return (
      <div className="grid gap-2">
        <Label className="text-xs text-muted-foreground">Allowed reference sources</Label>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {(Object.keys(REFERENCE_SOURCE_LABELS) as ReferenceSource[]).map((src) => (
            <label key={src} className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={current.has(src)}
                onCheckedChange={() => toggle(src)}
              />
              {REFERENCE_SOURCE_LABELS[src]}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={Boolean(field.multi)}
            onCheckedChange={(v) => onChange({ multi: v } as Partial<WizardField>)}
          />
          Allow multiple chips
        </label>
      </div>
    );
  }

  return null;
};

// ---------- Per-section editor ----------

const SectionEditor = ({
  section,
  workspaceId,
  onChange,
  onRemove,
}: {
  section: StructureSection;
  workspaceId: string | null;
  onChange: (patch: Partial<StructureSection>) => void;
  onRemove: () => void;
}) => (
  <div className="grid gap-2 rounded-md border p-3">
    <div className="flex items-start gap-2">
      <div className="flex-1 grid gap-1.5">
        {workspaceId ? (
          <>
            <ChipTextInput
              workspaceId={workspaceId}
              value={section.section}
              onChange={(v) => onChange({ section: v })}
              placeholder="Section name (type / for Corpus)"
            />
            <ChipTextInput
              workspaceId={workspaceId}
              value={section.angle ?? ""}
              onChange={(v) => onChange({ angle: v })}
              placeholder="Angle (type / for Corpus)"
            />
            <ChipTextInput
              workspaceId={workspaceId}
              value={section.goal ?? ""}
              onChange={(v) => onChange({ goal: v })}
              placeholder="Goal (type / for Corpus)"
            />
          </>
        ) : (
          <>
            <Input
              placeholder="Section name"
              value={section.section}
              onChange={(e) => onChange({ section: e.target.value })}
            />
            <Input
              placeholder="Angle"
              value={section.angle ?? ""}
              onChange={(e) => onChange({ angle: e.target.value })}
            />
            <Input
              placeholder="Goal"
              value={section.goal ?? ""}
              onChange={(e) => onChange({ goal: e.target.value })}
            />
          </>
        )}
      </div>
      <Button variant="ghost" size="icon" onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="grid gap-1.5">
        <Label className="text-xs text-muted-foreground">Target word count</Label>
        <Input
          type="number"
          value={section.target_word_count ?? ""}
          onChange={(e) =>
            onChange({
              target_word_count: e.target.value === "" ? null : Number(e.target.value) || null,
            })
          }
          placeholder="e.g. 600"
        />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs text-muted-foreground">Voice lean</Label>
        <Select
          value={section.voice_lean ?? VOICE_LEAN_INHERIT}
          onValueChange={(v) =>
            onChange({ voice_lean: v === VOICE_LEAN_INHERIT ? null : (v as VoiceLean) })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VOICE_LEAN_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">Must include (comma-separated)</Label>
      <Input
        value={listToCsv(section.must_include)}
        onChange={(e) => {
          const list = csvToList(e.target.value);
          onChange({ must_include: list.length > 0 ? list : null });
        }}
        placeholder="Facts, phrases, or references this section MUST include"
      />
    </div>

    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">Must avoid (comma-separated)</Label>
      <Input
        value={listToCsv(section.must_avoid)}
        onChange={(e) => {
          const list = csvToList(e.target.value);
          onChange({ must_avoid: list.length > 0 ? list : null });
        }}
        placeholder="Phrases or framings this section MUST NOT include"
      />
    </div>
  </div>
);
